import { Platform } from 'react-native';
import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import { refreshAppleTripSurfaceSnapshot } from '@/services/appleTripSurfaceBackgroundRefresh';
import { readCachedTripSurfaceSnapshot } from '@/services/appleWidgetV1';
import { useUserStore } from '@/state/userStore';

const ACTIVE_SYNC_MIN_INTERVAL_MS = 60 * 1000;
const ACTIVE_SYNC_MAX_INTERVAL_MS = 15 * 60 * 1000;
const SYNC_DEBOUNCE_MS = 5_000;

/**
 * Keeps widget / Live Activity snapshots fresh while the app runs, on foreground,
 * before backgrounding, and on a travel-day-aware interval — using live Firestore
 * when online (offline cache as fallback).
 */
export const useAppleTripSurfaceSync = (): void => {
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const lastSyncRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;
    if (!firebaseUser?.uid) return;

    const clearActiveInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const scheduleActiveInterval = (cadenceSeconds?: number) => {
      clearActiveInterval();
      const intervalMs = Math.min(
        ACTIVE_SYNC_MAX_INTERVAL_MS,
        Math.max(ACTIVE_SYNC_MIN_INTERVAL_MS, (cadenceSeconds ?? 900) * 1000),
      );
      intervalRef.current = setInterval(() => {
        void runSync(false);
      }, intervalMs);
    };

    const runSync = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastSyncRef.current < SYNC_DEBOUNCE_MS) return;
      lastSyncRef.current = now;
      try {
        await refreshAppleTripSurfaceSnapshot({
          userId: firebaseUser.uid,
          preferNetwork: true,
          fromServer: force,
        });
        const cached = await readCachedTripSurfaceSnapshot();
        if (AppState.currentState === 'active') {
          scheduleActiveInterval(cached?.liveActivity?.updateCadenceSeconds);
        }
      } catch {
        // Best-effort surface refresh.
      }
    };

    void runSync(true);

    const onAppStateChange = (nextState: AppStateStatus) => {
      if (nextState === 'background' || nextState === 'inactive') {
        clearActiveInterval();
        void runSync(true);
        return;
      }
      if (nextState !== 'active') {
        return;
      }
      void runSync(true);
      void readCachedTripSurfaceSnapshot().then((cached) => {
        if (AppState.currentState === 'active') {
          scheduleActiveInterval(cached?.liveActivity?.updateCadenceSeconds);
        }
      });
    };

    const subscription = AppState.addEventListener('change', onAppStateChange);
    if (AppState.currentState === 'active') {
      onAppStateChange('active');
    }

    return () => {
      subscription.remove();
      clearActiveInterval();
    };
  }, [firebaseUser?.uid]);
};
